import { ChatMessage, ChatMessageArtifact } from "@store/messages.store";

export const getFormattedMessageText = (text: string, type: string) => {
  if (type === "debug-json") {
    try {
      return `~~~json\n${JSON.stringify(JSON.parse(text || ""), null, 2)}`;
    } catch (error) {
      console.error(error);
      return text;
    }
  }
  return text;
};

const knownMessageTypes = [
  "text",
  "image-fallback",
  "debug-json",
  "tool-call",
  "file",
  "tool-calls",
  "reasoning",
] as const;

export type KnownMessageTypes = (typeof knownMessageTypes)[number];

export const isValidChatMessageType = (
  type: string,
): type is KnownMessageTypes => knownMessageTypes.some((t) => t === type);

const formatToolName = (toolName: string): string => {
  if (!toolName) return "";
  return toolName
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getArtifactDisplayName = (
  artifact: ChatMessageArtifact,
  artifacts: ChatMessage["artifacts"],
): string => {
  const artifactKind = artifact.kind || "unknown";

  const getKindLabel = (kind: string): string => {
    switch (kind) {
      case "tool-call-request":
        return "Tool Call";
      case "tool-call-response":
        return "Tool Response";
      case "active-widget-context":
        return "Widget Context";
      case "widget-cma-details":
        return "CMA Details Widget";
      case "widget-listing-details":
        return "Listing Details Widget";
      case "widget-listings-search":
        return "Listings Search Widget";
      default:
        return kind
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
    }
  };

  switch (artifact.kind) {
    case "tool-call-request": {
      const toolArtifact = artifact as any;
      const toolName = toolArtifact.content?.tool;
      const operationName =
        toolArtifact.content?.arguments?.operationData?.operationName;

      // Prefer operationName if available, fall back to tool name
      const displayValue = operationName || toolName;
      const formattedValue = displayValue ? formatToolName(displayValue) : null;

      return formattedValue
        ? `${getKindLabel(artifactKind)} - ${formattedValue}`
        : getKindLabel(artifactKind);
    }
    case "tool-call-response": {
      const responseArtifact = artifact as any;
      const callId = responseArtifact.content?.callId;

      // Find the corresponding tool-call-request to get the operation name
      if (callId && artifacts) {
        const correspondingRequest = artifacts.find(
          (a) =>
            a.kind === "tool-call-request" &&
            (a as any).content?.callId === callId,
        ) as any;

        if (correspondingRequest) {
          const operationName =
            correspondingRequest.content?.arguments?.operationData
              ?.operationName;
          const toolName = correspondingRequest.content?.tool;
          const displayValue = operationName || toolName;
          const formattedValue = displayValue
            ? formatToolName(displayValue)
            : null;

          if (formattedValue) {
            return `${getKindLabel(artifactKind)} - ${formattedValue}`;
          }
        }
      }

      return getKindLabel(artifactKind);
    }
    default: {
      return getKindLabel(artifactKind);
    }
  }
};

const toolCallActionLabels: Record<string, string> = {
  // Property search
  FindListingsV3: "Searching for listings",
  FindOnePropertyByAddress: "Looking up property details",
  GetListingDetailsByMlsId: "Fetching listing details",
  GetListingsMarketInsights: "Analyzing market insights",

  // CMA / Comps
  CreateComps: "Creating CMA",
  CreateCompsWithOffMarketProperty: "Creating Off-Market CMA",
  PatchComps: "Updating CMA",
  GetCompsDetails: "Fetching CMA details",
  ListMyComps: "Loading your CMAs",
  SearchComparables: "Searching for comparables",
  SearchComparablesByComp: "Searching for comparables",
  AddComparablesToComps: "Adding comparables",
  RemoveComparablesFromComps: "Removing comparables",
  GenerateCompsPresentation: "Generating CMA presentation",

  // Gmail
  SendEmail: "Sending email",
  CreateDraft: "Creating email draft",
  ListUnreadEmails: "Fetching emails",
  ListDrafts: "Fetching drafts",
  GetFullEmail: "Reading email",
  GetFullDraft: "Reading draft",
  GetEmailsBySender: "Fetching emails",

  // Calendar
  AddEventToMyCalendar: "Adding calendar event",
  DeleteEventOnMyCalendar: "Deleting calendar event",
  UpdateEventOnMyCalendar: "Updating calendar event",
  ListMyCalendars: "Fetching calendars",
  ListMyEvents: "Fetching calendar events",

  // Contacts
  ListContacts: "Loading contacts",
  SearchContacts: "Searching contacts",

  // Dotloop
  SendPromptToDotloopAssistant: "Consulting Dotloop",

  // Chat tools
  webSearch: "Searching the web",
  generateFile: "Generating file",
  analyzeFile: "Analyzing file",
  interpretCode: "Running analysis",
};

export const getToolCallActionLabel = (operationName: string): string => {
  if (!operationName) return "Processing request";
  return (
    toolCallActionLabels[operationName] ??
    `Working on ${formatToolName(operationName).toLowerCase()}`
  );
};

export const extractReasoningInfo = (
  text: string,
): { title: string; content: string }[] => {
  // Split by bold text (**title**) to extract multiple reasoning blocks
  const regex = /\*\*(.*?)\*\*/g;
  const blocks: { title: string; content: string }[] = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    const title = match[1];
    const contentStart = match.index + match[0].length;

    const nextMatch = regex.exec(text);
    const contentEnd = nextMatch ? nextMatch.index : text.length;

    const content = text.substring(contentStart, contentEnd).trim();

    blocks.push({ title, content });

    regex.lastIndex = contentEnd;
  }

  if (blocks.length === 0) {
    return [{ title: "Reasoning", content: text }];
  }

  return blocks;
};
